import { useEffect, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  bootstrapChartData,
  defaultOptions,
  getTimeScaleOptions
} from 'components/charts';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { cloneDeep, merge } from 'lodash';
import {
  createModal,
  DateRangePicker,
  LoadingIndicator,
  Tooltip
} from '@thermon-platforms/thermon-ui-core';
import api from 'util/api';
import useDevicesStore from 'store/devices';
import useSessionStore from 'store/session';
import ConfigModal from './settings-modal';


/**
 * @function CircuitsGroupChart
 * @description line chart for multi-circuit temperature data
 * @memberof module:view/Devices/Detail
 *
 * @param {object} props
 * @param {object} props.data
 * @returns React.Element
 *
 * @example
 * <CircuitsGroupChart data={data} loading={loading} />
 */
function CircuitsGroupChart({circuitIds, hideCurrent=false}) {
  const [sessionStore] = useSessionStore();
  const [devicesStore] = useDevicesStore();
  const now = new Date();
  const [range, setRange] = useState({
    start: new Date(now - (3 * 60 * 60 * 1000)).toISOString(),
    end: new Date(now).toISOString()
  });
  const [data, setData] = useState({
    temperature: bootstrapChartData(),
    current: bootstrapChartData()
  });
  const [loading, setLoading] = useState(false);
  const [tempUnit, setTempUnit] = useState();
  // To prevent race condition issues, live is a ref and not state
  const live = useRef(true);
  let interval;

  function parseData(data) {
    const series = {
      temperature: {
        datasets: []
      },
      current: {
        datasets: []
      }
    };

    (circuitIds || []).forEach(circuitId => {
      const circuit = devicesStore.shallowDevices.get(circuitId);
      const readings = data[circuitId]?.readingsDataByType;
      if (!readings) return;

      if (readings.Temperature) {
        series.temperature.datasets.push(Object.assign({label: circuit.model.name}, {
          data: readings.Temperature.data.map(item => {
            return {x: item.time, y: item.value}
          })
        }));
      }

      if (readings.Current) {
        series.current.datasets.push(Object.assign({label: circuit.model.name}, {
          data: readings.Current.data.map(item => {
            return {x: item.time, y: item.value}
          })
        }));
      }
    });

    return series;
  }

  // Make sure we handle any clean up
  const intervalRef = useRef();
  useEffect(() => {
    intervalRef.current = setInterval(setRangeToNow, 5 * 60 * 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
  }, []);

  // Handle side effects of the date range changing
  useEffect(() => {
    // If end date is within a minute of now, let's assume it's live
    const now = new Date();
    if (now - new Date(range.end) < (5 * 60 * 1000)) {
      live.current = true
    } else {
      live.current = false;
    }

    queryData();
  }, [range]);

  function queryData() {
    setLoading(true);
    const fidelity = Math.round(((new Date(range.end) - new Date(range.start)) / (60 * 1000)) * Number(sessionStore.chartFidelity));

    api(`/historicalData?startDate=${range.start}&endDate=${range.end}&circuitIds=${circuitIds.join('&circuitIds=')}&maxDataPoints=${fidelity}`)
      .then(async res => {
        const rawData = await res.json();
        setTempUnit(rawData[circuitIds[0]]?.readingsDataByType?.Temperature?.units);
        setData(parseData(rawData));
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function setRangeToNow() {
    const now = new Date();
    setRange({
      start: range.start || new Date(now - (60 * 60 * 1000)).toISOString(),
      end: new Date(now).toISOString()
    });
  }

  function setLive () {
    if (interval) clearInterval(interval);
    setRangeToNow();
    interval = setInterval(setRangeToNow, 5 * 60 * 1000);
    live.current = true;
  }

  // Wait for the chart fidelity to change before re-querying data
  const firstLoad = useRef(true);
  useEffect(() => {
    if (firstLoad.current) {
      firstLoad.current = false;
      return;
    }
    queryData();
  }, [sessionStore.chartFidelity]);

  function configureCharts() {
    const configModal = createModal(<ConfigModal />)

    configModal.open()
      .catch(() => {/* no-op */});
  }

  return (
    <>
      <div className="chart-container">
        <div className="controls">
          <DateRangePicker
            start={range.start}
            end={range.end}
            onChange={setRange}
          />
          <button
            onClick={configureCharts}
            className="settings"
            data-tooltip-content={t`Chart Settings`}
            data-tooltip-id="chart-tooltip"
            aria-label={t`Chart Settings`}
          >
            <Trans>Chart Settings</Trans>
          </button>
          <LoadingIndicator show={loading} />
          <span
            className={`live-indicator ${live.current ? 'active' : ''}`}
            onClick={setLive}
          >
            <Trans>Live</Trans>
          </span>
        </div>
        <div className="chart" style={{height: Math.max(300, 24*circuitIds.length)}}>
          <Line
            data={data?.temperature}
            options={
              merge(cloneDeep(defaultOptions.line), {
                spanGaps: 1000 * 60 * 60,
                plugins: {
                  legend: {
                    position: 'left',
                  }
                },
                scales: {
                  x: getTimeScaleOptions(),
                  y: {
                    min: 0,
                    type: 'linear',
                    position: 'left',
                    title: {
                      display: true,
                      text: t`Temperature` + ` (${tempUnit || '°C'})`
                    }
                  }
                }
              })
            }
          />
        </div>
        {!hideCurrent ? <div className="chart" style={{height: Math.max(300, 24*circuitIds.length)}}>
          <Line
            data={data?.current}
            options={
              merge(cloneDeep(defaultOptions.line), {
                spanGaps: 1000 * 60 * 60,
                plugins: {
                  legend: {
                    position: 'left',
                  }
                },
                scales: {
                  x: getTimeScaleOptions(),
                  y: {
                    min: 0,
                    max: 20,
                    type: 'linear',
                    position: 'left',
                    title: {
                      display: true,
                      text: t`Current` + ` (A)`
                    }
                  }
                }
              })
            }
          />
        </div> : <></>}
      </div>
      <Tooltip id="chart-tooltip" />
    </>
  );
}

export default CircuitsGroupChart;
